import { useState, useEffect, useCallback, useRef } from "react";

// Breakpoints matching Tailwind defaults
export const BREAKPOINTS = {
  sm: 640,
  md: 768,
  lg: 1024,
  xl: 1280,
  "2xl": 1536,
};

/**
 * Enhanced mobile detection hook with additional device info
 * @returns {Object} Device information
 */
export const useDevice = () => {
  const [device, setDevice] = useState({
    isMobile: false,
    isTablet: false,
    isDesktop: true,
    isTouch: false,
    orientation: "portrait",
    width: typeof window !== "undefined" ? window.innerWidth : 1024,
    height: typeof window !== "undefined" ? window.innerHeight : 768,
    breakpoint: "lg",
    safeAreaInsets: { top: 0, right: 0, bottom: 0, left: 0 },
  });

  useEffect(() => {
    const updateDevice = () => {
      const width = window.innerWidth;
      const height = window.innerHeight;
      const isTouch = "ontouchstart" in window || navigator.maxTouchPoints > 0;

      // Determine breakpoint
      let breakpoint = "2xl";
      if (width < BREAKPOINTS.sm) breakpoint = "xs";
      else if (width < BREAKPOINTS.md) breakpoint = "sm";
      else if (width < BREAKPOINTS.lg) breakpoint = "md";
      else if (width < BREAKPOINTS.xl) breakpoint = "lg";
      else if (width < BREAKPOINTS["2xl"]) breakpoint = "xl";

      // Get safe area insets (for notched devices)
      const computedStyle = getComputedStyle(document.documentElement);
      const safeAreaInsets = {
        top: parseInt(computedStyle.getPropertyValue("--sat") || "0") || 0,
        right: parseInt(computedStyle.getPropertyValue("--sar") || "0") || 0,
        bottom: parseInt(computedStyle.getPropertyValue("--sab") || "0") || 0,
        left: parseInt(computedStyle.getPropertyValue("--sal") || "0") || 0,
      };

      setDevice({
        isMobile: width < BREAKPOINTS.md,
        isTablet: width >= BREAKPOINTS.md && width < BREAKPOINTS.lg,
        isDesktop: width >= BREAKPOINTS.lg,
        isTouch,
        orientation: width > height ? "landscape" : "portrait",
        width,
        height,
        breakpoint,
        safeAreaInsets,
      });
    };

    updateDevice();
    window.addEventListener("resize", updateDevice);
    window.addEventListener("orientationchange", updateDevice);

    return () => {
      window.removeEventListener("resize", updateDevice);
      window.removeEventListener("orientationchange", updateDevice);
    };
  }, []);

  return device;
};

/**
 * Simple mobile check hook (backward compatible with use-mobile.js)
 */
export const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < BREAKPOINTS.md);
    };

    checkMobile();
    const mql = window.matchMedia(`(max-width: ${BREAKPOINTS.md - 1}px)`);
    mql.addEventListener("change", checkMobile);

    return () => mql.removeEventListener("change", checkMobile);
  }, []);

  return isMobile;
};

/**
 * Hook for handling swipe gestures
 * @param {Object} options - Swipe options
 * @returns {Object} Swipe handlers and state
 */
export const useSwipe = (options = {}) => {
  const {
    onSwipeLeft,
    onSwipeRight,
    onSwipeUp,
    onSwipeDown,
    threshold = 50,
    preventScroll = false,
  } = options;

  const touchStart = useRef({ x: 0, y: 0 });
  const touchEnd = useRef({ x: 0, y: 0 });
  const [swiping, setSwiping] = useState(false);
  const [direction, setDirection] = useState(null);

  const onTouchStart = useCallback((e) => {
    touchStart.current = {
      x: e.targetTouches[0].clientX,
      y: e.targetTouches[0].clientY,
    };
    touchEnd.current = { ...touchStart.current };
    setSwiping(true);
    setDirection(null);
  }, []);

  const onTouchMove = useCallback(
    (e) => {
      touchEnd.current = {
        x: e.targetTouches[0].clientX,
        y: e.targetTouches[0].clientY,
      };

      if (preventScroll) {
        const deltaX = touchStart.current.x - touchEnd.current.x;
        const deltaY = touchStart.current.y - touchEnd.current.y;

        // If horizontal swipe is more prominent, prevent scroll
        if (Math.abs(deltaX) > Math.abs(deltaY)) {
          e.preventDefault();
        }
      }
    },
    [preventScroll],
  );

  const onTouchEnd = useCallback(() => {
    setSwiping(false);

    const deltaX = touchStart.current.x - touchEnd.current.x;
    const deltaY = touchStart.current.y - touchEnd.current.y;
    const absX = Math.abs(deltaX);
    const absY = Math.abs(deltaY);

    // Determine if swipe meets threshold
    if (absX < threshold && absY < threshold) {
      return;
    }

    // Determine direction
    if (absX > absY) {
      // Horizontal swipe
      if (deltaX > threshold) {
        setDirection("left");
        onSwipeLeft?.();
      } else if (deltaX < -threshold) {
        setDirection("right");
        onSwipeRight?.();
      }
    } else {
      // Vertical swipe
      if (deltaY > threshold) {
        setDirection("up");
        onSwipeUp?.();
      } else if (deltaY < -threshold) {
        setDirection("down");
        onSwipeDown?.();
      }
    }
  }, [threshold, onSwipeLeft, onSwipeRight, onSwipeUp, onSwipeDown]);

  return {
    handlers: {
      onTouchStart,
      onTouchMove,
      onTouchEnd,
    },
    swiping,
    direction,
  };
};

/**
 * Hook for pull-to-refresh functionality
 * @param {Function} onRefresh - Callback when refresh is triggered
 * @param {Object} options - Configuration options
 * @returns {Object} Pull-to-refresh state and handlers
 */
export const usePullToRefresh = (onRefresh, options = {}) => {
  const { threshold = 80, resistance = 2.5 } = options;

  const [pulling, setPulling] = useState(false);
  const [pullDistance, setPullDistance] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const startY = useRef(0);
  const containerRef = useRef(null);

  const canPull = useCallback(() => {
    if (!containerRef.current) return false;
    return containerRef.current.scrollTop === 0;
  }, []);

  const onTouchStart = useCallback(
    (e) => {
      if (canPull()) {
        startY.current = e.touches[0].clientY;
        setPulling(true);
      }
    },
    [canPull],
  );

  const onTouchMove = useCallback(
    (e) => {
      if (!pulling || refreshing) return;

      const currentY = e.touches[0].clientY;
      const diff = currentY - startY.current;

      if (diff > 0 && canPull()) {
        // Apply resistance
        const distance = diff / resistance;
        setPullDistance(Math.min(distance, threshold * 1.5));

        if (distance > 10) {
          e.preventDefault();
        }
      }
    },
    [pulling, refreshing, canPull, resistance, threshold],
  );

  const onTouchEnd = useCallback(async () => {
    if (!pulling) return;

    if (pullDistance >= threshold && !refreshing) {
      setRefreshing(true);
      try {
        await onRefresh?.();
      } finally {
        setRefreshing(false);
      }
    }

    setPulling(false);
    setPullDistance(0);
  }, [pulling, pullDistance, threshold, refreshing, onRefresh]);

  return {
    containerRef,
    handlers: {
      onTouchStart,
      onTouchMove,
      onTouchEnd,
    },
    pulling,
    pullDistance,
    refreshing,
    progress: Math.min(pullDistance / threshold, 1),
  };
};

/**
 * Hook to handle keyboard visibility on mobile
 * Useful for adjusting layouts when virtual keyboard appears
 */
export const useKeyboardVisibility = () => {
  const [keyboardVisible, setKeyboardVisible] = useState(false);
  const [keyboardHeight, setKeyboardHeight] = useState(0);

  useEffect(() => {
    // Use visualViewport API if available (modern browsers)
    if (window.visualViewport) {
      const handleResize = () => {
        const heightDiff = window.innerHeight - window.visualViewport.height;
        const isVisible = heightDiff > 150; // Threshold to detect keyboard
        setKeyboardVisible(isVisible);
        setKeyboardHeight(isVisible ? heightDiff : 0);
      };

      window.visualViewport.addEventListener("resize", handleResize);
      return () =>
        window.visualViewport.removeEventListener("resize", handleResize);
    }

    // Fallback for older browsers
    const handleFocus = (e) => {
      if (e.target.tagName === "INPUT" || e.target.tagName === "TEXTAREA") {
        setKeyboardVisible(true);
      }
    };

    const handleBlur = () => {
      setKeyboardVisible(false);
      setKeyboardHeight(0);
    };

    document.addEventListener("focusin", handleFocus);
    document.addEventListener("focusout", handleBlur);

    return () => {
      document.removeEventListener("focusin", handleFocus);
      document.removeEventListener("focusout", handleBlur);
    };
  }, []);

  return { keyboardVisible, keyboardHeight };
};

/**
 * Hook to lock body scroll (useful for modals on mobile)
 */
export const useScrollLock = (lock = false) => {
  useEffect(() => {
    if (!lock) return;

    const originalStyle = window.getComputedStyle(document.body).overflow;
    const { scrollY } = window;

    document.body.style.overflow = "hidden";
    document.body.style.position = "fixed";
    document.body.style.top = `-${scrollY}px`;
    document.body.style.width = "100%";

    return () => {
      document.body.style.overflow = originalStyle;
      document.body.style.position = "";
      document.body.style.top = "";
      document.body.style.width = "";
      window.scrollTo(0, scrollY);
    };
  }, [lock]);
};

/**
 * Hook to detect if device prefers reduced motion
 */
export const usePrefersReducedMotion = () => {
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);

  useEffect(() => {
    const mediaQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
    setPrefersReducedMotion(mediaQuery.matches);

    const handleChange = (e) => setPrefersReducedMotion(e.matches);
    mediaQuery.addEventListener("change", handleChange);

    return () => mediaQuery.removeEventListener("change", handleChange);
  }, []);

  return prefersReducedMotion;
};

/**
 * Hook for long press detection (mobile context menus)
 */
export const useLongPress = (callback, options = {}) => {
  const { threshold = 500, onStart, onCancel } = options;

  const timerRef = useRef(null);
  const isLongPress = useRef(false);

  const start = useCallback(
    (e) => {
      isLongPress.current = false;
      onStart?.(e);

      timerRef.current = setTimeout(() => {
        isLongPress.current = true;
        callback(e);
      }, threshold);
    },
    [callback, threshold, onStart],
  );

  const clear = useCallback(
    (e, shouldTriggerCancel = true) => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      if (shouldTriggerCancel && !isLongPress.current) {
        onCancel?.(e);
      }
    },
    [onCancel],
  );

  return {
    onTouchStart: start,
    onTouchEnd: clear,
    onTouchMove: (e) => clear(e, true),
    onMouseDown: start,
    onMouseUp: clear,
    onMouseLeave: (e) => clear(e, false),
  };
};

/**
 * Hook for haptic feedback (on supported devices)
 */
export const useHaptics = () => {
  const vibrate = useCallback((pattern = 10) => {
    if ("vibrate" in navigator) {
      navigator.vibrate(pattern);
    }
  }, []);

  const light = useCallback(() => vibrate(10), [vibrate]);
  const medium = useCallback(() => vibrate(20), [vibrate]);
  const heavy = useCallback(() => vibrate([30, 10, 30]), [vibrate]);
  const success = useCallback(() => vibrate([10, 50, 10]), [vibrate]);
  const error = useCallback(() => vibrate([50, 30, 50, 30, 50]), [vibrate]);

  return { vibrate, light, medium, heavy, success, error };
};
